import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useContext, useRef } from 'react';
import { asArray } from '@olivierpascal/helpers';
import { useMergeRefs } from '@floating-ui/react';
import { createPolymorphicComponent, } from '../../helpers/react/polymorphicComponentTypes';
import { useStyles } from '../../hooks/useStyles';
import { commonStyles } from '../../helpers/commonStyles';
import { Base } from '../Base';
import { useVisualState } from '../VisualState';
import { StateLayer } from '../StateLayer';
import { FocusRing } from '../FocusRing';
import { Item } from '../Item';
import { ListContext } from '../List';
import { listItemVariantStyles } from './variants';
import { listItemFocusRingStyles, listItemItemStyles, listItemStateLayerStyles, listItemStyles, } from './ListItem.styles';
import { listItemTheme } from './ListItem.stylex';
// https://github.com/material-components/material-web/blob/main/list/internal/listitem/list-item.ts
export const ListItem = createPolymorphicComponent(forwardRef(function ListItem(props, forwardedRef) {
    const { as, styles, sx, innerStyles, variant = 'standard', visualState: visualStateProp, href, overline, start, children, supportingText, trailingSupportingText, end, disabled, selected: selectedProp, target: targetProp, leading, leadingIcon, leadingImage, leadingVideo, trailing, trailingIcon, onClick, size: sizeProp = 'md', noFocusRing: noFocusRingProp, maxLines, ...other } = props;
    const actionRef = useRef(null);
    const { visualState, setRef: setVisualStateRef } = useVisualState(visualStateProp, { disabled });
    const handleRef = useMergeRefs([
        forwardedRef,
        setVisualStateRef,
        actionRef,
    ]);
    const variantStyles = variant
        ? listItemVariantStyles[variant]
        : undefined;
    const { combineStyles, getStyles, globalStyles, settings } = useStyles({
        name: 'ListItem',
        styles: [listItemStyles, variantStyles, styles],
        visualState,
    });
    const listContext = useContext(ListContext);
    const type = href !== undefined ? 'link' : onClick ? 'button' : 'text';
    const role = type === 'text' ? 'listitem' : undefined;
    const selected = !disabled && selectedProp;
    const isInteractive = type !== 'text';
    const target = type === 'link' && targetProp ? targetProp : undefined;
    const size = listContext?.size ?? sizeProp;
    const adaptedSize = size === 'md' && (!!supportingText || !!leadingVideo) ? 'lg' : size;
    const noFocusRing = listContext?.noFocusRing ?? noFocusRingProp;
    const rootElement = as ??
        (type == 'link'
            ? (settings?.linkAs ?? 'a')
            : type === 'button'
                ? 'button'
                : 'li');
    const renderContainer = () => (_jsxs(_Fragment, { children: [_jsx("div", { ...getStyles('background', selected && 'background$selected', disabled && 'background$disabled') }), isInteractive ? (_jsxs(_Fragment, { children: [_jsx(StateLayer, { styles: [
                            listItemStateLayerStyles,
                            ...asArray(innerStyles?.stateLayer),
                        ], for: actionRef, disabled: disabled, visualState: visualState }), noFocusRing ? null : (_jsx(FocusRing, { styles: [
                            listItemFocusRingStyles,
                            ...asArray(innerStyles?.focusRing),
                        ], for: actionRef, visualState: visualState, inward: true }))] })) : null] }));
    const renderStart = () => start ??
        (leadingIcon ? (_jsx("div", { ...getStyles('icon', 'icon$leading', disabled && 'icon$leading$disabled', selected && 'icon$leading$selected'), children: leadingIcon })) : leadingImage ? (_jsx("div", { ...getStyles('image', commonStyles.backgroundImage(leadingImage)) })) : leadingVideo ? (_jsx("video", { ...getStyles('video'), autoPlay: !disabled, loop: true, muted: true, children: leadingVideo.map((video, videoIndex) => (_jsx("source", { src: video.src, type: video.type }, videoIndex))) })) : (leading));
    const renderEnd = () => end ??
        (trailingIcon ? (_jsx("div", { ...getStyles('icon', 'icon$trailing', disabled && 'icon$trailing$disabled', selected && 'icon$trailing$selected'), children: trailingIcon })) : (trailing));
    return (_jsx(Base, { as: rootElement, role: role, type: type === 'button' ? 'button' : undefined, tabIndex: disabled || !isInteractive ? -1 : 0, disabled: disabled, "aria-current": selected, href: href, target: target, onClick: onClick, ...other, visualState: visualState, sx: [
            listItemTheme,
            globalStyles,
            combineStyles('host', `host$${adaptedSize}`, isInteractive && 'host$interactive', selected && 'host$selected', disabled && 'host$disabled', !start && !leadingVideo && 'host$leadingSpace', !end && 'host$trailingSpace'),
            sx,
        ], ref: handleRef, children: _jsx(Item, { styles: [listItemItemStyles, ...asArray(innerStyles?.item)], container: renderContainer(), overline: overline, start: renderStart(), supportingText: supportingText, trailingSupportingText: trailingSupportingText, end: renderEnd(), maxLines: maxLines, children: children }) }));
}));
